import type { OrderStatus, Service } from '@/design-system';
import { toClient, type Client } from '@/entities/client/model';
import { toProperty, type Property } from '@/entities/property/model';
import { formatCurrency } from '@/shared/lib';

import {
    ORDER_STATUS_CODES,
    SERVICE_DELIVERIES,
    type InspectionTypeResource,
    type OrderDocumentResource,
    type OrderResource,
    type OrderServiceResource,
    type OrderStatusCode,
    type OrderTimelineResource,
} from './types';

/**
 * Een keuring ("order") zoals de schermen hem zien. Eén keuring hoort bij één
 * object en één opdrachtgever, heeft één of meer diensten en (uiteindelijk) een
 * rapport en een factuur.
 *
 * `toOrder` is de enige plek waar de lijnvorm uit `types.ts` wordt vertaald.
 */

export { ORDER_STATUS_CODES, type OrderStatusCode };

/** De woorden uit `docs/woordenlijst.md`, gelijk aan `OrderStatus::label()` in de API. */
export const ORDER_STATUS_LABELS: Record<OrderStatusCode, string> = {
    new: 'Nieuw',
    confirmed: 'Bevestigd',
    scheduled: 'Ingepland',
    surveying: 'In opname',
    draft_report: 'Concept rapport',
    completed: 'Afgerond',
    invoiced: 'Gefactureerd',
    cancelled: 'Geannuleerd',
};

/** De levenscyclus, in volgorde; `cancelled` staat erbuiten. */
export const ORDER_LIFECYCLE_CODES = [
    'new',
    'confirmed',
    'scheduled',
    'surveying',
    'draft_report',
    'completed',
    'invoiced',
] as const satisfies readonly OrderStatusCode[];

export function isOrderStatusCode(value: string): value is OrderStatusCode {
    return (ORDER_STATUS_CODES as readonly string[]).includes(value);
}

/**
 * De kleur van de statuspil. Het ontwerp kent zeven statuskleuren onder
 * Nederlandse sleutels; een geannuleerde keuring krijgt de neutrale toon en dus
 * geen achtste kleur.
 */
const DESIGN_STATUS: Record<OrderStatusCode, OrderStatus | null> = {
    new: 'nieuw',
    confirmed: 'bevestigd',
    scheduled: 'ingepland',
    surveying: 'opname',
    draft_report: 'concept',
    completed: 'voltooid',
    invoiced: 'gefactureerd',
    cancelled: null,
};

export function designStatusFor(status: OrderStatusCode): OrderStatus | null {
    return DESIGN_STATUS[status];
}

/** Energielabels openen het NTA 8800-scherm; de rest de opname. */
const ENERGY_LABEL_CODES = new Set(['nta8800', 'energielabel']);

/**
 * De code van de NHG-bijlage, gelijk aan `ServiceDeliveries::NHG_APPENDIX` in de API.
 * De bijlage is een eigen dienst met een eigen tarief, maar hij wordt nooit los
 * opgenomen: hij komt in het rapport van de bouwtechnische keuring.
 */
export const NHG_APPENDIX_CODE = 'nhg';

/** De code van de bouwtechnische keuring: de dienst waar de NHG-bijlage bij hoort. */
export const BUILDING_SURVEY_CODE = 'nta8060';

/** De dienstkleur in planning en legenda: keuring, energielabel of NEN 2767. */
export function serviceForCode(code: string | null | undefined): Service {
    if (code && ENERGY_LABEL_CODES.has(code)) {
        return 'energielabel';
    }

    return code === 'nen2767' ? 'nen2767' : 'keuring';
}

/** Een dienst zoals deze regel hem nodig heeft: de code bepaalt de plaats, de naam de tekst. */
interface NamedService {
    code: string | null;
    name: string;
}

/**
 * De diensten van een keuring op één regel, overal met dezelfde woorden: in de
 * werklijst, op de planning, in het dossier en bij het aanmaken.
 *
 * Diensten staan naast elkaar met `+`. De NHG-bijlage niet: die staat niet náást
 * de keuring maar hoort erbij, dus "Bouwtechnische keuring incl. NHG-bijlage".
 * Alleen de bijlage besteld? Dan blijft het gewoon zijn eigen naam.
 */
export function serviceLabelOf(services: readonly NamedService[]): string {
    const named = services.filter((service) => service.name !== '');
    const appendix = named.find((service) => service.code === NHG_APPENDIX_CODE);
    const rest = named.filter((service) => service !== appendix);

    if (rest.length === 0) {
        return named.map((service) => service.name).join(' + ');
    }

    const label = rest.map((service) => service.name).join(' + ');

    return appendix ? `${label} incl. ${appendix.name}` : label;
}

export { SERVICE_DELIVERIES };

export type ServiceDelivery = (typeof SERVICE_DELIVERIES)[number];

export interface OrderService {
    typeUuid: string | null;
    code: string | null;
    name: string;
    service: Service;
    /** `pending` · `in_progress` · `completed` · `cancelled` */
    status: string;
    statusLabel: string;
    templateVersionUuid: string | null;
    /** Opname, in het rapport of buiten Smartkeur; de API beslist, de app rekent het niet na. */
    delivery: ServiceDelivery;
    /** Mag deze sessie de dienst nu klaar melden (`POST .../services/{inspectionType}/complete`)? */
    canComplete: boolean;
    inspection: {
        uuid: string;
        status: string;
        statusLabel: string;
        /** Heeft de opname een vragenlijst? Alleen dan valt er iets in te vullen. */
        hasQuestionnaire: boolean;
        /**
         * Mag deze sessie de ingediende opname weer openzetten? De eigenaar en de inspecteur
         * van de opname, ook na verzending; de API beslist, de app rekent het niet na.
         */
        canReopen: boolean;
    } | null;
    price: string | null;
    inspectorFee: string | null;
    credits: number | null;
}

/** Omzet en marge. Alleen wie geld mag zien krijgt dit blok; anders `null`. */
export interface OrderFinancials {
    price: string | null;
    inspectorFee: string | null;
    vatRate: string | null;
    margin: string | null;
    creditsReserved: number | null;
    creditsConsumed: number | null;
    internalNotes: string | null;
}

export interface Person {
    uuid: string;
    name: string;
}

export interface Order {
    uuid: string;
    number: string;
    status: OrderStatusCode;
    statusLabel: string;
    /** Uit de payload; de app houdt geen eigen kopie van de toestandsmachine bij. */
    allowedTransitions: OrderStatusCode[];
    source: string;
    requestedAt: string | null;
    scheduledAt: string | null;
    scheduledEndAt: string | null;
    completedAt: string | null;
    cancelledAt: string | null;
    cancelledReason: string | null;
    notes: string | null;
    client: Client;
    property: Property;
    assignee: Person | null;
    services: OrderService[];
    /** "Bouwtechnische keuring (NTA 8060)", meerdere diensten met " + ". */
    serviceLabel: string;
    /** De dienstkleur van de eerste dienst. */
    service: Service;
    /** De opname die "Opname openen" opent: de eerste dienst met een opname met vragenlijst. */
    inspection: { uuid: string; service: Service; statusLabel: string } | null;
    financials: OrderFinancials | null;
    /**
     * Het rapport is al bij de klant: "Adres van het object wijzigen" is dicht
     * (behalve voor platformbeheer). Alleen `GET /orders/{order}` zegt het; elders `false`.
     */
    addressLocked: boolean;
}

function toOrderService(resource: OrderServiceResource): OrderService {
    const code = resource.inspection_type?.code ?? null;

    return {
        typeUuid: resource.inspection_type?.uuid ?? null,
        code,
        name: resource.inspection_type?.name ?? resource.status_label,
        service: serviceForCode(code),
        status: resource.status,
        statusLabel: resource.status_label,
        templateVersionUuid: resource.template_version_uuid,
        delivery: resource.delivery ?? 'survey',
        canComplete: resource.can_complete ?? false,
        inspection: resource.inspection
            ? {
                  uuid: resource.inspection.uuid,
                  status: resource.inspection.status,
                  statusLabel: resource.inspection.status_label,
                  hasQuestionnaire: resource.inspection.has_questionnaire ?? true,
                  canReopen: resource.inspection.can_reopen ?? false,
              }
            : null,
        price: resource.price ?? null,
        inspectorFee: resource.inspector_fee ?? null,
        credits: resource.credits ?? null,
    };
}

export function toOrder(resource: OrderResource): Order {
    const services = [...resource.services]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(toOrderService);
    const withInspection = services.find(hasSurvey);
    const showsMoney = resource.price !== undefined;

    return {
        uuid: resource.uuid,
        number: resource.number,
        status: resource.status,
        statusLabel: resource.status_label,
        allowedTransitions: resource.allowed_transitions.filter(isOrderStatusCode),
        source: resource.source,
        requestedAt: resource.requested_at,
        scheduledAt: resource.scheduled_at,
        scheduledEndAt: resource.scheduled_end_at,
        completedAt: resource.completed_at,
        cancelledAt: resource.cancelled_at,
        cancelledReason: resource.cancelled_reason,
        notes: resource.notes,
        addressLocked: resource.address_locked ?? false,
        client: toClient(resource.client),
        property: toProperty(resource.property),
        assignee: resource.assigned_user,
        services,
        serviceLabel: serviceLabelOf(services),
        service: services[0]?.service ?? 'keuring',
        inspection:
            withInspection?.inspection != null
                ? {
                      uuid: withInspection.inspection.uuid,
                      service: withInspection.service,
                      statusLabel: withInspection.inspection.statusLabel,
                  }
                : null,
        financials: showsMoney
            ? {
                  price: resource.price ?? null,
                  inspectorFee: resource.inspector_fee ?? null,
                  vatRate: resource.vat_rate ?? null,
                  margin: resource.margin ?? null,
                  creditsReserved: resource.credits_reserved ?? null,
                  creditsConsumed: resource.credits_consumed ?? null,
                  internalNotes: resource.internal_notes ?? null,
              }
            : null,
    };
}

/**
 * Heeft deze dienst een opname om in te vullen? Een opname zonder vragenlijst (van vóór die
 * regel) telt niet: daar valt niets te doen, dus geen tabblad en geen "Opname openen".
 */
export function hasSurvey(
    service: OrderService,
): service is OrderService & { inspection: NonNullable<OrderService['inspection']> } {
    return (
        service.delivery === 'survey' &&
        service.inspection !== null &&
        service.inspection.hasQuestionnaire
    );
}

/**
 * De diensten die buiten Smartkeur worden gemaakt en nog niet klaar zijn gemeld, zoals een
 * energielabel. Ze houden het rapport niet tegen, maar de keuring is pas helemaal klaar als
 * ook zij klaar zijn.
 */
export function openServices(order: Pick<Order, 'services'>): OrderService[] {
    return order.services.filter(
        (service) =>
            service.delivery === 'manual' &&
            service.status !== 'completed' &&
            service.status !== 'cancelled',
    );
}

/* -------------------------------------------------------------------------- */
/* Tijdlijn en documenten                                                      */
/* -------------------------------------------------------------------------- */

export interface TimelineEntry {
    key: string;
    type: string;
    label: string;
    at: string | null;
    /** Wie het deed; `null` bij een automatische stap of als het onbekend is. */
    actorName: string | null;
    automatic: boolean;
}

export function toTimeline(resource: OrderTimelineResource): TimelineEntry[] {
    return resource.events.map((event, index) => ({
        key: `${event.type}-${index}`,
        type: event.type,
        label: event.label,
        at: event.occurred_at,
        actorName: event.actor?.name ?? null,
        automatic: event.automatic,
    }));
}

export const DOCUMENT_KINDS = ['report', 'invoice', 'attachment'] as const;

export type DocumentKind = (typeof DOCUMENT_KINDS)[number] | 'other';

export interface OrderDocument {
    uuid: string;
    kind: DocumentKind;
    name: string;
    /** "PDF · 240 kB", of de stand van zaken ("Wordt gegenereerd"). */
    meta: string | null;
    statusLabel: string | null;
    mimeType: string | null;
    createdAt: string | null;
    /** Kortlevende link; `null` zolang er geen bestand is. */
    downloadUrl: string | null;
    thumbnailUrl: string | null;
}

export function toOrderDocument(resource: OrderDocumentResource): OrderDocument {
    return {
        uuid: resource.uuid,
        kind: (DOCUMENT_KINDS as readonly string[]).includes(resource.kind)
            ? (resource.kind as DocumentKind)
            : 'other',
        name: resource.name,
        meta: resource.meta,
        statusLabel: resource.status_label,
        mimeType: resource.mime_type,
        createdAt: resource.created_at,
        downloadUrl: resource.download_url,
        thumbnailUrl: resource.thumbnail_url,
    };
}

/* -------------------------------------------------------------------------- */
/* Diensten en inspecteurs, voor formulieren                                   */
/* -------------------------------------------------------------------------- */

export interface InspectionType {
    uuid: string;
    code: string;
    name: string;
    service: Service;
    /**
     * Krijgt deze dienst een eigen regel op de factuur? Dat is zo zodra er een tarief
     * voor staat, ook een tarief van € 0. Zonder tarief is de dienst inbegrepen bij de
     * keuring waarmee hij verkocht wordt en komt hij niet op de factuur. Zegt de API het
     * niet (een oudere cache), dan gaan we uit van een eigen regel: dat is wat elk
     * bedrijf met ingevulde tarieven ziet.
     */
    billedSeparately: boolean;
    /** Wat het tarief kost, of `null` als je de tarieven niet mag zien. */
    price: string | null;
}

export function toInspectionTypes(resources: readonly InspectionTypeResource[]): InspectionType[] {
    return [...resources]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((resource) => ({
            uuid: resource.uuid,
            code: resource.code,
            name: resource.name,
            service: serviceForCode(resource.code),
            billedSeparately: resource.billed_separately ?? true,
            price: resource.price ?? null,
        }));
}

/* -------------------------------------------------------------------------- */
/* Opmaak                                                                      */
/* -------------------------------------------------------------------------- */

/** `"1450.00"` -> `"€ 1.450"`, `"389.00"` met `cents` -> `"€ 389,00"`. */
export function formatMoney(
    amount: string | number | null | undefined,
    options: { cents?: boolean } = {},
): string {
    if (amount === null || amount === undefined || amount === '') {
        return '—';
    }

    const value = typeof amount === 'number' ? amount : Number(amount);

    return Number.isFinite(value) ? formatCurrency(value, options) : '—';
}

/** Wachttijd in de mono-kolom van "Wacht op afronding": `26 u`. */
export function formatWaiting(hours: number): string {
    if (hours < 1) {
        return '< 1 u';
    }

    return `${Math.round(hours)} u`;
}
